/* eslint-disable jsx-a11y/iframe-has-title */
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMapMarkerAlt, faEnvelope, faPhone } from '@fortawesome/free-solid-svg-icons'
import { faInstagram, faFacebook, faWhatsapp } from '@fortawesome/free-brands-svg-icons'
import {
  Box, Typography, Link, TextField, Button,
  Container, Grid, Stack, IconButton,
} from '@mui/material'
import contactImage from '../assets/contact.jpg'

const FIELD_SX = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    bgcolor: '#f8fafc',
    '& fieldset': { borderColor: '#e2e8f0' },
    '&:hover fieldset': { borderColor: '#94a3b8' },
    '&.Mui-focused fieldset': { borderColor: '#2563eb' },
    '&.Mui-focused': { bgcolor: '#fff' },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: '#2563eb' },
}

const InfoRow = ({ icon, iconColor, iconBg, label, children }) => (
  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
    <Box sx={{
      width: 44, height: 44, borderRadius: '12px', bgcolor: iconBg,
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    }}>
      <FontAwesomeIcon icon={icon} style={{ color: iconColor, fontSize: '1.05rem' }} />
    </Box>
    <Box>
      <Typography sx={{ color: 'rgba(255,255,255,0.38)', fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.4, mb: 0.4 }}>
        {label}
      </Typography>
      {children}
    </Box>
  </Box>
)

const Contact = () => {
  const { t } = useTranslation()
  const [formData, setFormData] = useState({ name: '', age: '', phone: '', message: '' })

  const handleChange = e => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = e => {
    e.preventDefault()
    const { name, age, phone, message } = formData
    const templateMessage = `Halo CV Mulia Utama Mandiri,\n\nNama : ${name}\nUsia : ${age}\nNo. Handphone : ${phone}\nPesan : ${message}\n\nSaya mendapatkan informasi ini dari website Anda.\n\nTerima kasih.`
    window.open(`https://wa.me/${t('footer.number')}?text=${encodeURIComponent(templateMessage.trim())}`, '_blank')
  }

  return (
    <Box sx={{ bgcolor: '#f8fafc', overflow: 'hidden' }}>

      {/* ── HERO ── */}
      <Box sx={{
        position: 'relative',
        minHeight: { xs: '42vh', md: '52vh' },
        display: 'flex',
        alignItems: 'flex-end',
        backgroundImage: `url(${contactImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}>
        <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(10,15,30,0.92) 0%, rgba(10,15,30,0.3) 55%, transparent 100%)' }} />
        <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: 'linear-gradient(90deg, #2563eb, #f97316)' }} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, pb: { xs: 6, md: 8 } }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <Box sx={{ width: 20, height: 2, bgcolor: '#f97316', borderRadius: 1 }} />
            <Typography sx={{ color: '#f97316', fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 2 }}>
              CV. Mulia Utama Mandiri
            </Typography>
          </Stack>
          <Typography variant="h2" sx={{
            color: '#fff', fontWeight: 900,
            fontSize: { xs: '2.4rem', md: '3.8rem' },
            lineHeight: 1.05, letterSpacing: '-0.03em', mb: 1.5,
          }}>
            {t('contact.header.title')}
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.95rem', maxWidth: 480, lineHeight: 1.7 }}>
            {t('contact.header.description')}
          </Typography>
        </Container>
      </Box>

      {/* ── MAIN CONTENT ── */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Grid container spacing={3} alignItems="stretch">

          {/* LEFT — dark info panel */}
          <Grid item xs={12} md={5}>
            <Box sx={{
              bgcolor: '#0f172a',
              borderRadius: '24px',
              overflow: 'hidden',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              minHeight: { md: 580 },
            }}>
              <Box sx={{ height: 4, background: 'linear-gradient(90deg, #2563eb, #f97316)' }} />

              <Box sx={{ p: { xs: 3.5, md: 4.5 }, flexGrow: 1 }}>
                <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: '1.35rem', letterSpacing: '-0.02em', mb: 4.5 }}>
                  {t('contact.header.title')}
                </Typography>

                <Stack spacing={3.5} sx={{ mb: 4.5 }}>
                  <InfoRow icon={faMapMarkerAlt} iconColor="#f97316" iconBg="rgba(249,115,22,0.14)" label={t('contact.location.title')}>
                    <Link
                      href={t('contact.location.mapLink')} target="_blank" rel="noopener noreferrer" underline="none"
                      sx={{ color: 'rgba(255,255,255,0.82)', fontSize: '0.87rem', lineHeight: 1.6, display: 'block', '&:hover': { color: '#f97316' }, transition: '0.15s' }}
                    >
                      {t('contact.location.address')}
                    </Link>
                  </InfoRow>

                  <InfoRow icon={faEnvelope} iconColor="#60a5fa" iconBg="rgba(37,99,235,0.14)" label="Email">
                    <Link
                      href={`mailto:${t('contact.emailAndContact.email')}`} underline="none"
                      sx={{ color: 'rgba(255,255,255,0.82)', fontSize: '0.87rem', '&:hover': { color: '#60a5fa' }, transition: '0.15s' }}
                    >
                      {t('contact.emailAndContact.email')}
                    </Link>
                  </InfoRow>

                  <InfoRow icon={faPhone} iconColor="#4ade80" iconBg="rgba(34,197,94,0.14)" label="WhatsApp">
                    <Typography sx={{ color: 'rgba(255,255,255,0.82)', fontSize: '0.87rem' }}>
                      {t('contact.emailAndContact.phone')}
                    </Typography>
                  </InfoRow>
                </Stack>

                {/* Social links */}
                <Box>
                  <Typography sx={{ color: 'rgba(255,255,255,0.38)', fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.4, mb: 1.5 }}>
                    {t('contact.socialMedia.title')}
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    {[
                      { icon: faInstagram, href: t('contact.socialMedia.instagramLink'), hover: '#e1306c' },
                      { icon: faFacebook,  href: t('contact.socialMedia.facebookLink'),  hover: '#1877f2' },
                      { icon: faWhatsapp,  href: `https://wa.me/${t('footer.number')}`,   hover: '#25d366' },
                    ].map(({ icon, href, hover }) => (
                      <IconButton
                        key={href}
                        component="a" href={href} target="_blank" rel="noopener noreferrer"
                        sx={{
                          width: 40, height: 40, borderRadius: '10px',
                          bgcolor: 'rgba(255,255,255,0.07)',
                          color: 'rgba(255,255,255,0.55)',
                          '&:hover': { bgcolor: hover, color: '#fff' },
                          transition: 'all 0.2s ease',
                        }}
                      >
                        <FontAwesomeIcon icon={icon} style={{ fontSize: '0.95rem' }} />
                      </IconButton>
                    ))}
                  </Stack>
                </Box>
              </Box>

              {/* Embedded map flush at bottom */}
              <Box sx={{ height: 200, flexShrink: 0 }}>
                <iframe
                  src={t('contact.googleMap.iframeSrc')}
                  width="100%"
                  height="100%"
                  loading="lazy"
                  style={{ display: 'block', border: 'none', filter: 'grayscale(15%) contrast(1.05)' }}
                />
              </Box>
            </Box>
          </Grid>

          {/* RIGHT — form panel */}
          <Grid item xs={12} md={7}>
            <Box sx={{
              bgcolor: '#fff',
              borderRadius: '24px',
              border: '1px solid #e2e8f0',
              p: { xs: 3.5, md: 5 },
              height: '100%',
              boxShadow: '0 4px 32px rgba(0,0,0,0.06)',
              display: 'flex',
              flexDirection: 'column',
            }}>
              <Box sx={{ mb: 4 }}>
                <Typography sx={{ fontWeight: 900, color: '#0f172a', fontSize: '1.6rem', letterSpacing: '-0.03em', mb: 0.75 }}>
                  {t('contact.form.title')}
                </Typography>
                <Typography sx={{ color: '#64748b', fontSize: '0.88rem', lineHeight: 1.65 }}>
                  Isi formulir di bawah dan kami akan segera membalas melalui WhatsApp.
                </Typography>
              </Box>

              <Box component="form" onSubmit={handleSubmit} sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                <Grid container spacing={2.5} sx={{ flexGrow: 1 }}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth label={t('contact.form.fields.name.label')}
                      name="name" required value={formData.name} onChange={handleChange} sx={FIELD_SX}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth label={t('contact.form.fields.age.label')}
                      name="age" required value={formData.age} onChange={handleChange} sx={FIELD_SX}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth label={t('contact.form.fields.phone.label')}
                      name="phone" required value={formData.phone} onChange={handleChange} sx={FIELD_SX}
                    />
                  </Grid>
                  <Grid item xs={12} sx={{ flexGrow: 1 }}>
                    <TextField
                      fullWidth label={t('contact.form.fields.message.label')}
                      name="message" required multiline rows={6}
                      value={formData.message} onChange={handleChange} sx={FIELD_SX}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      type="submit" fullWidth variant="contained"
                      startIcon={<FontAwesomeIcon icon={faWhatsapp} />}
                      sx={{
                        py: 1.7, borderRadius: '12px',
                        bgcolor: '#0f172a', fontSize: '0.95rem', fontWeight: 700, textTransform: 'none',
                        boxShadow: '0 8px 24px -6px rgba(15,23,42,0.35)',
                        '&:hover': { bgcolor: '#2563eb', boxShadow: '0 8px 24px -6px rgba(37,99,235,0.4)' },
                        transition: 'all 0.2s ease',
                      }}
                    >
                      {t('contact.form.submitButton')}
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </Grid>

        </Grid>
      </Container>
    </Box>
  )
}

export default Contact
